import { AddressInfo } from "./types";
import {
  base58DecodedLength,
  checkBase58Address,
  decodeSegwitAddress,
} from "./addressChecksum";

// ─── Base58 character set (no 0, O, I, l) ─────────────────────────
const BASE58_CHARS =
  /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/;

// ─── Bech32 character set ──────────────────────────────────────────
const BECH32_CHARS = /^[qpzry9x8gf2tvdw0s3jn54khce6mua7l]+$/;

// ─── Detection helpers ─────────────────────────────────────────────

function looksLikeEmail(input: string): boolean {
  // Simplified email-like check: something@something.something
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(input);
}

function looksLikeBolt11(input: string): boolean {
  const lower = input.toLowerCase();
  return (
    lower.startsWith("lnbc") ||
    lower.startsWith("lntb") ||
    lower.startsWith("lnbcrt") ||
    lower.startsWith("lnsb")
  );
}

function looksLikeLnurl(input: string): boolean {
  return input.toLowerCase().startsWith("lnurl1");
}

function looksLikeBitcoinCash(input: string): boolean {
  const lower = input.toLowerCase();
  return (
    lower.startsWith("bitcoincash:") ||
    lower.startsWith("bchreg:") ||
    lower.startsWith("bchtest:")
  );
}

// ─── Non-Bitcoin address detection ─────────────────────────────────

function looksLikeEthereum(addr: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(addr);
}

function looksLikeSolana(addr: string): boolean {
  // Solana: base58, 32-44 characters, no leading 0x
  return addr.length >= 32 && addr.length <= 44 && BASE58_CHARS.test(addr);
}

function looksLikeRipple(addr: string): boolean {
  return /^r[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{24,34}$/.test(
    addr,
  );
}

function looksLikeLitecoinLegacy(addr: string): boolean {
  return /^[LM][123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{25,33}$/.test(
    addr,
  );
}

function looksLikeLitecoinSegwit(addr: string): boolean {
  const lower = addr.toLowerCase();
  if (!lower.startsWith("ltc1")) return false;
  const data = lower.slice(4);
  return data.length >= 38 && data.length <= 58 && BECH32_CHARS.test(data);
}

function looksLikeDogecoin(addr: string): boolean {
  return /^D[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{25,33}$/.test(
    addr,
  );
}

function looksLikeTron(addr: string): boolean {
  // Tron: starts with T, 34 chars, base58
  return /^T[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{33}$/.test(
    addr,
  );
}

function looksLikeCosmos(addr: string): boolean {
  return (
    addr.toLowerCase().startsWith("cosmos1") &&
    addr.length >= 39 &&
    addr.length <= 45
  );
}

function looksLikeCardano(addr: string): boolean {
  return addr.toLowerCase().startsWith("addr1") && addr.length >= 50;
}

function looksLikeMonero(addr: string): boolean {
  return /^4[0-9AB][123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{93}$/.test(
    addr,
  );
}

// ─── Bitcoin address detection (checksum-verified) ─────────────────

const LOOKS_LIKE_BASE58_BITCOIN =
  /^[13mn2][123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{24,34}$/;

function invalidBitcoinAddress(reason: string): AddressInfo {
  return {
    type: "bitcoin_invalid",
    label: "Invalid Bitcoin Address",
    isBitcoin: false,
    description: `This looks like a Bitcoin address, but it ${reason}. Do NOT send Bitcoin to it. Compare every character with the original, or ask the recipient to send the address again.`,
    isValidFormat: false,
    details: [
      "Every Bitcoin address contains a checksum, so a single wrong, missing or swapped character makes it invalid.",
      "Most wallets refuse to send to an invalid address, but don't rely on that.",
      "Copy and paste addresses instead of typing them, and double-check the first and last few characters.",
    ],
  };
}

/** Returns null when the input isn't shaped like a Bitcoin address at all. */
function identifyBitcoinAddress(input: string): AddressInfo | null {
  const lower = input.toLowerCase();

  if (lower.startsWith("bc1") || lower.startsWith("tb1")) {
    const segwit = decodeSegwitAddress(input);
    if (segwit.ok === false) return invalidBitcoinAddress(segwit.reason);

    const { network, version, programLength } = segwit;

    if (network === "testnet") {
      if (version === 0) {
        return {
          type: "bitcoin_testnet_segwit",
          label: "Bitcoin Testnet SegWit Address",
          isBitcoin: true,
          description:
            "This is a Bitcoin Testnet SegWit address — used for testing, not real funds.",
          isValidFormat: true,
          details: [
            "Format: Bech32, starts with tb1q",
            "Checksum: valid",
            "⚠️ This is a TESTNET address — it cannot receive real Bitcoin.",
          ],
        };
      }
      if (version === 1 && programLength === 32) {
        return {
          type: "bitcoin_testnet_taproot",
          label: "Bitcoin Testnet Taproot Address",
          isBitcoin: true,
          description:
            "This is a Bitcoin Testnet Taproot address — used for testing, not real funds.",
          isValidFormat: true,
          details: [
            "Format: Bech32m, starts with tb1p",
            "Checksum: valid",
            "⚠️ This is a TESTNET address — it cannot receive real Bitcoin.",
          ],
        };
      }
    }

    if (network === "mainnet" && version === 1 && programLength === 32) {
      return {
        type: "bitcoin_taproot",
        label: "Bitcoin Taproot Address (P2TR)",
        isBitcoin: true,
        description:
          "This is a Bitcoin Taproot (P2TR) address — the newest Bitcoin address format, activated in November 2021. Taproot addresses provide enhanced privacy, lower fees, and support for advanced smart contracts.",
        isValidFormat: true,
        details: [
          "Format: Bech32m (BIP-350), starts with bc1p",
          "Type: Pay-to-Taproot (P2TR, SegWit v1)",
          "Length: 62 characters",
          "Checksum: valid",
          "Taproot combines Schnorr signatures with Merkelized Alternative Script Trees (MAST).",
          "Provides the best on-chain privacy and smallest transaction sizes.",
        ],
      };
    }

    if (network === "mainnet" && version === 0 && programLength === 20) {
      return {
        type: "bitcoin_segwit_v0",
        label: "Bitcoin SegWit Address (P2WPKH)",
        isBitcoin: true,
        description:
          "This is a Bitcoin Native SegWit (P2WPKH) address — the most commonly used modern Bitcoin address format. SegWit addresses offer lower transaction fees compared to legacy formats.",
        isValidFormat: true,
        details: [
          "Format: Bech32 (BIP-173), starts with bc1q",
          "Type: Pay-to-Witness-Public-Key-Hash (P2WPKH, SegWit v0)",
          "Length: 42 characters",
          "Checksum: valid",
          "SegWit was activated in August 2017 and reduces transaction fees by ~30-40%.",
        ],
      };
    }

    if (network === "mainnet" && version === 0 && programLength === 32) {
      return {
        type: "bitcoin_segwit_p2wsh",
        label: "Bitcoin SegWit Script Address (P2WSH)",
        isBitcoin: true,
        description:
          "This is a Bitcoin Native SegWit script (P2WSH) address. P2WSH addresses are mostly used by multi-signature wallets and Lightning channels.",
        isValidFormat: true,
        details: [
          "Format: Bech32 (BIP-173), starts with bc1q",
          "Type: Pay-to-Witness-Script-Hash (P2WSH, SegWit v0)",
          "Length: 62 characters",
          "Checksum: valid",
          "Often used for multi-sig wallets and Lightning Network channel funding outputs.",
        ],
      };
    }

    // Checksum is fine, but no wallet uses this witness version / program length
    // today, and coins sent to it can be spent by anyone.
    return invalidBitcoinAddress(
      `uses SegWit version ${version} with a ${programLength}-byte program, which no Bitcoin wallet uses today (coins sent there could be lost)`,
    );
  }

  if (!LOOKS_LIKE_BASE58_BITCOIN.test(input)) return null;

  const base58 = checkBase58Address(input);
  if (base58.ok === false) {
    // A 32-byte base58 string is a Solana-style public key, not a broken
    // Bitcoin address. Let the non-Bitcoin checks have it.
    if (base58DecodedLength(input) === 32) return null;
    return invalidBitcoinAddress("fails its checksum (likely a typo)");
  }

  // The version byte decides the type, so it can disagree with the first character
  // only in contrived cases. Trust the decoded version byte.
  if (base58.network === "testnet") {
    return base58.type === "p2pkh"
      ? {
          type: "bitcoin_testnet_p2pkh",
          label: "Bitcoin Testnet Legacy Address",
          isBitcoin: true,
          description:
            "This is a Bitcoin Testnet legacy address — used for testing, not real funds.",
          isValidFormat: true,
          details: [
            "Format: Base58Check, starts with m or n",
            "Checksum: valid",
            "⚠️ This is a TESTNET address — it cannot receive real Bitcoin.",
          ],
        }
      : {
          type: "bitcoin_testnet_p2sh",
          label: "Bitcoin Testnet P2SH Address",
          isBitcoin: true,
          description:
            "This is a Bitcoin Testnet P2SH address — used for testing, not real funds.",
          isValidFormat: true,
          details: [
            "Format: Base58Check, starts with 2",
            "Checksum: valid",
            "⚠️ This is a TESTNET address — it cannot receive real Bitcoin.",
          ],
        };
  }

  if (base58.type === "p2sh") {
    return {
      type: "bitcoin_legacy_p2sh",
      label: "Bitcoin P2SH Address",
      isBitcoin: true,
      description:
        "This is a Bitcoin P2SH (Pay-to-Script-Hash) address. P2SH addresses start with '3' and can represent multi-signature wallets or wrapped SegWit addresses (P2SH-P2WPKH).",
      isValidFormat: true,
      details: [
        "Format: Base58Check, starts with 3",
        "Type: Pay-to-Script-Hash (P2SH, BIP-16)",
        "Length: 25-34 characters",
        "Checksum: valid",
        "Often used for multi-sig wallets or as a SegWit compatibility wrapper.",
        "Higher fees than native SegWit (bc1q) addresses.",
      ],
    };
  }

  return {
    type: "bitcoin_legacy_p2pkh",
    label: "Bitcoin Legacy Address (P2PKH)",
    isBitcoin: true,
    description:
      "This is a Bitcoin Legacy (P2PKH) address — the original Bitcoin address format created by Satoshi Nakamoto. While still valid, legacy addresses have the highest transaction fees.",
    isValidFormat: true,
    details: [
      "Format: Base58Check, starts with 1",
      "Type: Pay-to-Public-Key-Hash (P2PKH)",
      "Length: 25-34 characters",
      "Checksum: valid",
      "This is the oldest Bitcoin address format, used since 2009.",
      "For lower fees, consider using a SegWit (bc1q) or Taproot (bc1p) address instead.",
    ],
  };
}

// ─── Main detection function ───────────────────────────────────────

export function identifyAddress(raw: string): AddressInfo {
  const input = raw.trim();

  if (!input) {
    return {
      type: "unknown",
      label: "Empty Input",
      isBitcoin: false,
      description: "Please enter an address to check.",
      isValidFormat: false,
      details: [],
    };
  }

  // 1. Lightning Address (email-like)
  if (looksLikeEmail(input)) {
    return {
      type: "lightning_address",
      label: "Lightning Address",
      isBitcoin: true,
      description:
        "This looks like a Lightning Address — a human-readable way to receive Bitcoin payments over the Lightning Network, similar to an email address.",
      isValidFormat: true,
      details: [
        `User: ${input.split("@")[0]}`,
        `Domain: ${input.split("@")[1]}`,
        "Lightning Addresses use the LNURL-pay protocol under the hood.",
        "The address server will be queried to confirm this address is active.",
      ],
    };
  }

  // 2. BOLT11 Invoice
  if (looksLikeBolt11(input)) {
    const lower = input.toLowerCase();
    let network = "mainnet";
    if (lower.startsWith("lntb")) network = "testnet";
    else if (lower.startsWith("lnbcrt")) network = "regtest";
    else if (lower.startsWith("lnsb")) network = "simnet";

    return {
      type: "bolt11_invoice",
      label: "BOLT11 Lightning Invoice",
      isBitcoin: true,
      description:
        "This is a BOLT11 Lightning Network invoice — a one-time payment request used to receive Bitcoin over the Lightning Network.",
      isValidFormat: input.length > 20,
      details: [
        `Network: ${network}`,
        "BOLT11 invoices encode payment amount, destination, expiry time, and routing hints.",
        "Use the Rizful Invoice Decoder to see full invoice details.",
      ],
    };
  }

  // 3. LNURL
  if (looksLikeLnurl(input)) {
    return {
      type: "lnurl",
      label: "LNURL",
      isBitcoin: true,
      description:
        "This is an LNURL — an encoded URL used for Lightning Network payment flows. LNURLs can represent pay requests, withdrawal links, or authentication challenges.",
      isValidFormat: input.length > 10,
      details: [
        "LNURL is bech32-encoded and decodes to a URL.",
        "Common uses: LNURL-pay, LNURL-withdraw, LNURL-auth.",
      ],
    };
  }

  // 4. Bitcoin Cash
  if (looksLikeBitcoinCash(input)) {
    return {
      type: "bitcoin_cash",
      label: "Bitcoin Cash Address",
      isBitcoin: false,
      description:
        "This looks like a Bitcoin Cash (BCH) address. Bitcoin Cash is a separate cryptocurrency from Bitcoin (BTC) — they are NOT the same. Sending BTC to a BCH address will result in lost funds.",
      isValidFormat: true,
      details: [
        "Bitcoin Cash forked from Bitcoin in August 2017.",
        "BCH uses a different address format (cashaddr) to prevent confusion.",
        "⚠️ Do NOT send Bitcoin (BTC) to this address.",
      ],
    };
  }

  // 5. Bitcoin addresses (mainnet + testnet), checksum-verified
  const bitcoin = identifyBitcoinAddress(input);
  if (bitcoin) return bitcoin;

  // 7. Non-Bitcoin addresses
  if (looksLikeEthereum(input)) {
    return {
      type: "ethereum",
      label: "Ethereum Address",
      isBitcoin: false,
      description:
        "This appears to be an Ethereum (ETH) address. Ethereum is a completely different blockchain from Bitcoin. Do NOT send Bitcoin to an Ethereum address — the funds will be permanently lost.",
      isValidFormat: true,
      details: [
        "Format: Hex with 0x prefix, 42 characters total",
        "Ethereum uses account-based addresses (not UTXO like Bitcoin).",
        "Also used by EVM-compatible chains: Polygon, Arbitrum, Optimism, BSC, Avalanche C-Chain, etc.",
        "⚠️ This is NOT a Bitcoin address.",
      ],
    };
  }

  if (looksLikeTron(input)) {
    return {
      type: "tron",
      label: "TRON Address",
      isBitcoin: false,
      description:
        "This appears to be a TRON (TRX) address. TRON is a separate blockchain from Bitcoin, commonly used for USDT (Tether) transfers. Do NOT send Bitcoin to a TRON address.",
      isValidFormat: true,
      details: [
        "Format: Base58, starts with T, 34 characters",
        "TRON is widely used for USDT-TRC20 stablecoin transfers.",
        "⚠️ This is NOT a Bitcoin address.",
      ],
    };
  }

  if (looksLikeCosmos(input)) {
    return {
      type: "cosmos",
      label: "Cosmos Address",
      isBitcoin: false,
      description:
        "This appears to be a Cosmos (ATOM) address. Cosmos is a separate blockchain ecosystem from Bitcoin.",
      isValidFormat: true,
      details: [
        "Format: Bech32, starts with cosmos1",
        "Cosmos uses the Inter-Blockchain Communication (IBC) protocol.",
        "⚠️ This is NOT a Bitcoin address.",
      ],
    };
  }

  if (looksLikeCardano(input)) {
    return {
      type: "cardano",
      label: "Cardano Address",
      isBitcoin: false,
      description:
        "This appears to be a Cardano (ADA) address. Cardano is a separate blockchain from Bitcoin.",
      isValidFormat: true,
      details: [
        "Format: Bech32, starts with addr1",
        "⚠️ This is NOT a Bitcoin address.",
      ],
    };
  }

  if (looksLikeRipple(input)) {
    return {
      type: "ripple_xrp",
      label: "Ripple (XRP) Address",
      isBitcoin: false,
      description:
        "This appears to be a Ripple (XRP) address. XRP is a completely different network from Bitcoin. Do NOT send Bitcoin to an XRP address.",
      isValidFormat: true,
      details: [
        "Format: Base58, starts with r, 25-35 characters",
        "XRP uses a centralized validator-based consensus, unlike Bitcoin's proof-of-work.",
        "⚠️ This is NOT a Bitcoin address.",
      ],
    };
  }

  if (looksLikeLitecoinSegwit(input)) {
    return {
      type: "litecoin_segwit",
      label: "Litecoin SegWit Address",
      isBitcoin: false,
      description:
        "This appears to be a Litecoin (LTC) SegWit address. Litecoin is a separate cryptocurrency from Bitcoin, though it shares similar technology.",
      isValidFormat: true,
      details: [
        "Format: Bech32, starts with ltc1",
        "Litecoin is a fork of Bitcoin's codebase but uses a separate blockchain.",
        "⚠️ This is NOT a Bitcoin address.",
      ],
    };
  }

  if (looksLikeLitecoinLegacy(input)) {
    return {
      type: "litecoin_legacy",
      label: "Litecoin Legacy Address",
      isBitcoin: false,
      description:
        "This appears to be a Litecoin (LTC) legacy address. Litecoin is a separate cryptocurrency from Bitcoin.",
      isValidFormat: true,
      details: [
        "Format: Base58, starts with L or M, 25-34 characters",
        "⚠️ This is NOT a Bitcoin address.",
      ],
    };
  }

  if (looksLikeDogecoin(input)) {
    return {
      type: "dogecoin",
      label: "Dogecoin Address",
      isBitcoin: false,
      description:
        "This appears to be a Dogecoin (DOGE) address. Dogecoin is a separate cryptocurrency from Bitcoin — originally created as a joke in 2013.",
      isValidFormat: true,
      details: [
        "Format: Base58, starts with D, 25-34 characters",
        "Dogecoin has no supply cap (inflationary), unlike Bitcoin's 21 million limit.",
        "⚠️ This is NOT a Bitcoin address.",
      ],
    };
  }

  if (looksLikeMonero(input)) {
    return {
      type: "monero",
      label: "Monero Address",
      isBitcoin: false,
      description:
        "This appears to be a Monero (XMR) address. Monero is a privacy-focused cryptocurrency separate from Bitcoin.",
      isValidFormat: true,
      details: [
        "Format: Base58, starts with 4, 95 characters",
        "Monero uses ring signatures and stealth addresses for privacy.",
        "⚠️ This is NOT a Bitcoin address.",
      ],
    };
  }

  // Solana check last (since base58 overlaps with many formats)
  if (looksLikeSolana(input) && base58DecodedLength(input) === 32) {
    return {
      type: "solana",
      label: "Solana Address (Probable)",
      isBitcoin: false,
      description:
        "This appears to be a Solana (SOL) address. Solana is a completely different blockchain from Bitcoin. Do NOT send Bitcoin to a Solana address.",
      isValidFormat: true,
      details: [
        "Format: Base58, 32-44 characters",
        "Solana uses a different consensus mechanism (proof-of-history + proof-of-stake).",
        "Also used for Solana SPL tokens (e.g., USDC-SPL, USDT-SPL).",
        "⚠️ This is NOT a Bitcoin address.",
      ],
    };
  }

  // Nothing matched
  return {
    type: "unknown",
    label: "Unrecognized Format",
    isBitcoin: false,
    description:
      "This input doesn't match any known Bitcoin, Lightning, or common cryptocurrency address format. Please double-check for typos or extra spaces.",
    isValidFormat: false,
    details: [
      "Supported Bitcoin formats: Legacy (1...), P2SH (3...), SegWit (bc1q...), Taproot (bc1p...)",
      "Supported Lightning formats: Lightning Address (user@domain), BOLT11 invoice (lnbc...), LNURL",
      "Also detects: Ethereum (0x...), Solana, Litecoin, Dogecoin, XRP, TRON, Monero, Cardano, Cosmos",
    ],
  };
}
